import { useTheme } from "@emotion/react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useAssetSentinelData } from "../AssetSentinel/useAssetSentinelData";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { Box, Table, TableBody, TableCell, TableRow } from "@mui/material";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import { AMSModules, paginationStyle } from "../../utils/constants";
import { StatusBadge } from "../../utils/functions";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { getRowsPerPageOptions } from "../../utils/functions";
import { makeStyles } from "@mui/styles";
import InspectionsTableToolbar from "./InspectionsTableToolbar";
import AddEditInspectionDialog from "./AddEditInspectionDialog";
import InspectionDetailDialog from "./InspectionDetailDialog";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
	make: [],
	model: [],
	year: [],
	department: [],
	employee: [],
	funding: [],
};

const useStyles = makeStyles((theme) => ({
	title: {
		color: theme.palette.text.primary,
		fontWeight: 600,
	},
	colorTitle: {
		color: theme.palette.mode === "dark" ? "#fff" : "#3b3e66",
	},
	cardBackground: {
		background:
			theme.palette.mode === "dark" ? "#1e2e3a" : "rgb(242, 242, 242)",
	},
}));

const Inspections = () => {
	const classes = useStyles();

	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();
	const { inspections, vehicles, fundingSources, codes, employees, checklistItems, setWorkOrders, setInspections, setVehicles } =
		useAssetSentinelData();
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);

	const total = inspections?.length || 0;
	const loading = false;

	const [search, setSearch] = useState("");
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.inspections || initialFilterState,
	);
	const [openDialog, setOpenDialog] = useState(false);
	const [showDetail, setShowDetail] = useState(false);
	const [selectedInspection, setSelectedInspection] = useState(null)

	const currentAgency = useRef(null);

	const baseColumns = useMemo(
		() => [
			{
				field: "id",
				headerName: "ID",
				width: 80,
				sortable: true,
			},
			{
				field: "vehicleID",
				headerName: "Vehicle",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <>{`${row?.vehicleID}`}</>;
				},
			},
			{
				field: "officerID",
				headerName: "Employee",
				width: 150,
				sortable: true,
			},
			{
				field: "type",
				headerName: "Type",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.type} />;
				},
			},
			{
				field: "date",
				headerName: "Date & Time",
				width: 150,
				sortable: true,
			},
			{
				field: "odometer",
				headerName: "Odometer",
				width: 150,
				sortable: true,
			},
			{
				field: "pass",
				headerName: "Pass",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return row?.responses?.filter(
						(response) => response.status === "Pass",
					)?.length;
				},
			},
			{
				field: "fail",
				headerName: "Fail",
				width: 100,
				sortable: true,
				renderCell: ({ row }) => {
					return row?.responses?.filter(
						(response) => response.status === "Fail",
					)?.length;
				},
			},
			{
				field: "overallStatus",
				headerName: "Result",
				width: 100,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.overallStatus} />;
				},
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 120,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Inspection"
											// onClick={() =>
											// 	history.push(
											// 		`${PATH_APPLICANTS_EDIT}${row.id}`,
											// 	)
											// }
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Inspection"
											// onClick={() => {
											// 	setSelectedId(row.id);
											// 	setOpenDeleteDialog(true);
											// }}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["inspectionsTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["inspectionsTableColumnOrder"]),
				);
			}

			if (settingsMap["inspectionsTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["inspectionsTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["inspectionsTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["inspectionsTableColumnVisibility"]),
				);
			}

			if (settingsMap["inspectionsTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["inspectionsTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			// Avoid unnecessary state updates if sort model didn't change
			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"inspectionsTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch();
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"inspectionsTableColumnOrder",
					JSON.stringify(newColumnOrder),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => {
			return {
				field: col.field,
				width: col.width,
			};
		});
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"inspectionsTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"inspectionsTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.Inspections, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.Inspections, payload: initialFilterState });
	};

	// Stats
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const todayCount = inspections.filter(
		(i) => new Date(i.date) >= today,
	).length;
	const failedThisWeek = inspections.filter((i) => {
		const d = new Date(i.date);
		const weekAgo = new Date(Date.now() - 7 * 86400000);
		return i.overallStatus === "Fail" && d >= weekAgo;
	}).length;
	const inspectedVehicleIDs = new Set(
		inspections
			.filter(
				(i) => new Date(i.date) >= new Date(Date.now() - 24 * 3600000),
			)
			.map((i) => i.vehicleID),
	);
	const noInspection24h = vehicles.filter(
		(v) => v.status === "In Service" && !inspectedVehicleIDs.has(v.id),
	).length;

	return (
		<>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(3,1fr)",
					gap: 14,
					marginBottom: 22,
				}}
			>
				<div
					className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} blue`}
				>
					<div className="kpi-label">Inspections Today</div>
					<div className="kpi-value">{todayCount}</div>
					<div className="kpi-sub">completed this shift</div>
				</div>
				<div
					className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} ${noInspection24h > 0 ? "yellow" : "green"}`}
				>
					<div className="kpi-label">No Inspection (24h)</div>
					<div className="kpi-value">{noInspection24h}</div>
					<div className="kpi-sub">in-service vehicles</div>
				</div>
				<div
					className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} ${failedThisWeek > 0 ? "red" : "green"}`}
				>
					<div className="kpi-label">Failed This Week</div>
					<div className="kpi-value">{failedThisWeek}</div>
					<div className="kpi-sub">inspection failures</div>
				</div>
			</div>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)", // or any other dynamic value subtracting top nav height
					marginTop: theme.spacing(0.5),
				}}
			>
				<InspectionsTableToolbar
					openAdd={() => setOpenDialog(true)}
					search={search}
					setSearch={setSearch}
					// status={status}
					// vehicles={vehicles}
					// setStatus={setStatus}
					// codes={codes}
					// employees={employees}
					// fundingSources={fundingSources}
					// serviceFilter={serviceFilter}
					// setServiceFilter={setServiceFilter}
					// assignmentFilter={assignmentFilter}
					// setAssignmentFilter={setAssignmentFilter}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						rows={inspections}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						// getRowId={(row) => row.GUID}
						getRowId={(row) => row.id}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						onRowClick={(params) => {
							setSelectedInspection(params?.row);
							setShowDetail(true);
						}}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
						}}
					/>
				</Box>
			</Box>

			<AddEditInspectionDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onSave={() => undefined}
				vehicles={vehicles}
				employees={employees}
				checklistItems={checklistItems}
				inspectionTypeOptions={[]}
				// defaultVehicleID,
				// defaultOfficerID,
				// defaultOdometer,
			/>

			<InspectionDetailDialog
				open={showDetail}
				onClose={() => setShowDetail(false)}
				inspection={selectedInspection}
				vehicles={vehicles}
				employees={employees}
				checklistItems={checklistItems}
				setWorkOrders={setWorkOrders}
				setVehicles={setVehicles}
				setInspections={setInspections}
				onUpdateInspection={() => undefined}
				workOrderTypeOptions={codes?.workOrderTypes}
				fundingSourceOptions={fundingSources}
			/>
		</>
	);
};

export default Inspections;
